import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { CustomWidgetVariant } from '@kemu-io/kemu-types';
import { buildWidgetRequestBody, dbEntityToCollection, decodeServerErrorInfo } from './utils';
import widgetCache from './widgetCache';
import { WidgetCollectionState, WidgetCollectionItem } from '@src/app/reducers/widget/widgetSlice';
import widgetApi from '@src/api/widget/widgetApi';
import { editCustomWidgetState } from '@src/app/recipe/utils';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';
import { showGlobalNotification } from '@src/features/interface/interfaceSlice';

/**
 * Updates the given widget in the database and uploads the contents to S3.
 * It also adds the updated version of the widget to the local cache.
*/
export const updateWidgetAction = createAsyncThunk('/widgets/updateWidgetAction', async (
	args: {
		widgetDbId: string;
		widgetRecipeId: string;
		recipeId: string;
		thingId: string;
		variant: CustomWidgetVariant;
	},
	thunkAPI
): Promise<WidgetCollectionItem | null> => {

	try {
		const t = getGlobalIntl();
	
		const {
			requestObject,
			contentsStr,
			widgetState
		} = buildWidgetRequestBody({
			widgetId: args.widgetRecipeId,
			thingId: args.thingId,
			recipeId: args.recipeId,
			translateFunction: t,
			variant: args.variant,
		});
	
	
		thunkAPI.dispatch(showGlobalNotification({
			code: 'update_widget',
			message: t('Widget.AsyncIndicator.Progress', { action: t('CommonWords.Updating') }),
			showProgress: true,
			duration: 30,
			type: 'info',
		}));
	
		const response = await widgetApi.updateWidget({
			widgetId: args.widgetDbId,
			widgetInfo: {
				info: requestObject,
				rawContents: contentsStr,
			},
		});
	
		// Store the widget locally
		await widgetCache.storeWidget(response.widget.id, response.widget.version, contentsStr);
	
		// Update the reference in the recipe
		editCustomWidgetState(args.recipeId, args.thingId, args.widgetRecipeId, {
			...widgetState,
			collectionInfo: {
				userId: response.widget.userId,
				version: response.widget.version,
				widgetId: response.widget.id
			}
		}, true);
	
		thunkAPI.dispatch(showGlobalNotification({
			code: 'update_widget',
			message: t('Widget.AsyncIndicator.Success', { action: t('CommonWords.Updated') }),
			type: 'success',
		}));

		return dbEntityToCollection(contentsStr, response.widget);
	}catch (error) {
		const t = getGlobalIntl();
		console.error('Failed to update widget:', error);
		const { message } = decodeServerErrorInfo(error);
		thunkAPI.dispatch(showGlobalNotification({
			code: 'update_widget',
			title: t('Widget.AsyncIndicator.Failure.Title', { action: t('CommonWords.Update') }),
			message: message,
			type: 'error',
		}));

		return null;
	}
});


export const updateWidgetReducer = ((builder: ActionReducerMapBuilder<WidgetCollectionState>/*, thunk: any*/): void => {

	builder.addCase(updateWidgetAction.fulfilled, (state, action) => {
		if(!action.payload){
			return;
		}


		const otherWidgets = state.customWidgets.filter(widget => widget.dbId !== action.payload?.dbId);

		state.customWidgets = [
			...otherWidgets,
			action.payload
		];

	});
});

